import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The organization's extra-time switch on Billing — plan-017 WAPP-A (design §29.11 row 5; owner answers OD-B, PQ-1,
 * PQ-2, PQ-3; ADR 0055).
 *
 * Shown to whoever reads the organization's finance; FLIPPED only by its Owner, Admin or Finance
 * (`account.finance.manage`, PQ-2) — anyone else sees the setting and who may change it. Off for every organization
 * until someone turns it on (PQ-1).
 *
 * Only where the terms in force include minutes (PQ-3): under pay-as-you-go every minute is already paid time, so the
 * card says "Every minute is charged at your rates." and draws no switch.
 *
 * A change names the revision it read (`If-Match`). When someone changed it first the API answers
 * `409 revision_stale`: the card reloads the setting and says so, and changes nothing on its own — the person decides
 * again on what is stored now. Each change is audited (`account.extra_time_changed`), and the card says that too.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { TALK_RATE_QUERY_KEY, type ExtraTimeView } from "../../api/slice/talk-rate.js";
import { roleHolds, useOpenAccount } from "../account-admin/overview/account-overview-screen.js";
import { SwitchControl } from "./switch-control.js";

export function ExtraTimeCard(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const [stale, setStale] = useState(false);
  const extraTime = useQuery({
    queryKey: TALK_RATE_QUERY_KEY.extraTime,
    queryFn: () => gateway.readExtraTime(),
    retry: false,
  });
  const change = useMutation({
    mutationFn: (next: { readonly enabled: boolean; readonly revision: number }) => gateway.changeExtraTime(next.enabled, next.revision),
    onMutate: () => {
      setStale(false);
    },
    onSuccess: (stored: ExtraTimeView) => {
      queryClient.setQueryData(TALK_RATE_QUERY_KEY.extraTime, stored);
    },
    onError: async (raised) => {
      if (raised instanceof ApiError && raised.code === "revision_stale") {
        setStale(true);
        await queryClient.invalidateQueries({ queryKey: TALK_RATE_QUERY_KEY.extraTime });
      }
    },
  });

  return (
    <div className="bl-card">
      {extraTime.isPending ? (
        <p className="bl-card-note">{portalText("finance", "billing.extra_time.loading")}</p>
      ) : extraTime.data === undefined ? (
        <>
          <p className="bl-card-kicker">{portalText("finance", "billing.extra_time.title")}</p>
          <p className="bl-card-note" role="status">{portalText("finance", "billing.extra_time.unavailable")}</p>
        </>
      ) : !extraTime.data.termsIncludeMinutes ? (
        <>
          <p className="bl-card-kicker">{portalText("finance", "billing.extra_time.title")}</p>
          <p className="bl-card-line">{portalText("finance", "billing.extra_time.payg")}</p>
        </>
      ) : (
        <ExtraTimeSwitch
          view={extraTime.data}
          canManage={canManage}
          pending={change.isPending}
          onChange={(enabled, revision) => {
            change.mutate({ enabled, revision });
          }}
          problem={
            stale
              ? portalText("finance", "billing.extra_time.stale")
              : change.isError
                ? apiProblemText(change.error, portalText("finance", "billing.extra_time.failed"))
                : null
          }
        />
      )}
    </div>
  );
}

interface ExtraTimeSwitchProps {
  readonly view: ExtraTimeView;
  readonly canManage: boolean;
  readonly pending: boolean;
  readonly onChange: (enabled: boolean, revision: number) => void;
  readonly problem: string | null;
}

function ExtraTimeSwitch({ view, canManage, pending, onChange, problem }: ExtraTimeSwitchProps): ReactNode {
  usePortalTranslation("finance");
  return (
    <>
      <div className="bl-auto-head">
        <span className="bl-auto-title">
          <span className="bl-card-kicker">{portalText("finance", "billing.extra_time.title")}</span>
          <span className="bl-auto-state">
            {view.enabled ? portalText("finance", "billing.extra_time.on") : portalText("finance", "billing.extra_time.off")}
          </span>
        </span>
        <SwitchControl
          label={portalText("finance", "billing.extra_time.switch")}
          labelVisible={false}
          checked={view.enabled}
          disabled={!canManage || pending}
          onChange={(enabled) => {
            onChange(enabled, view.revision);
          }}
        />
      </div>
      <p className="bl-card-note bl-card-note-spaced">{portalText("finance", "billing.extra_time.in_progress")}</p>
      <p className="bl-card-note">
        {canManage ? portalText("finance", "billing.extra_time.audit") : portalText("finance", "billing.extra_time.who")}
      </p>
      {problem === null ? null : (
        <p className="bl-card-note" role="status">
          {problem}
        </p>
      )}
    </>
  );
}
